import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { CONTACT } from "@/data/content";
import { Phone, Mail, Linkedin, Github, Download, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const EMPTY = { name: "", email: "", subject: "", message: "" };

const validate = (form) => {
    const errors = {};
    if (form.name.trim().length < 2) errors.name = "Please enter your name.";
    if (!EMAIL_RE.test(form.email.trim())) errors.email = "Please enter a valid email address.";
    if (form.subject.trim().length < 2) errors.subject = "Please enter a subject.";
    if (form.message.trim().length < 10) errors.message = "Message must be at least 10 characters.";
    return errors;
};

const inputClass =
    "w-full border border-white/10 bg-black/40 px-4 py-3.5 font-mono2 text-sm text-zinc-200 placeholder:text-zinc-600 transition-[border-color,box-shadow] duration-300 focus:border-neon/60 focus:shadow-[0_0_18px_rgba(0,255,102,0.07)] focus:outline-none";

export const Contact = () => {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setErrors((er) => ({ ...er, [key]: undefined }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(form);
        if (Object.keys(errs).length) {
            setErrors(errs);
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name.trim(),
                email: form.email.trim(),
                subject: form.subject.trim(),
                message: form.message.trim(),
            });
            toast.success("Message sent — thank you. Harshdeep will get back to you soon.");
            setForm(EMPTY);
            setErrors({});
        } catch (err) {
            toast.error("Could not send your message. Please try again or reach out directly by email.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="scroll-mt-20 py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeader
                    chapter="09"
                    kicker="Reach Out"
                    title="Let's Connect"
                    subtitle="Interested in cybersecurity, penetration testing, security research, or technology? Let's connect."
                    testId="contact-heading"
                />

                <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
                    <Reveal className="lg:col-span-5">
                        <div className="space-y-4">
                            <a
                                href={CONTACT.phoneHref}
                                data-testid="contact-phone-link"
                                className="group flex items-center gap-4 border border-white/10 bg-surface/70 p-5 transition-[border-color] duration-300 hover:border-neon/40"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-neon/30 bg-neon/5 text-neon">
                                    <Phone size={17} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-500">Phone</span>
                                    <span className="font-mono2 text-sm text-zinc-200 transition-colors duration-300 group-hover:text-neon">{CONTACT.phone}</span>
                                </span>
                            </a>
                            <a
                                href={CONTACT.emailHref}
                                data-testid="contact-email-link"
                                className="group flex items-center gap-4 border border-white/10 bg-surface/70 p-5 transition-[border-color] duration-300 hover:border-neon/40"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-neon/30 bg-neon/5 text-neon">
                                    <Mail size={17} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-500">Email</span>
                                    <span className="font-mono2 text-sm text-zinc-200 transition-colors duration-300 group-hover:text-neon">{CONTACT.email}</span>
                                </span>
                            </a>
                            <a
                                href={CONTACT.linkedinHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-linkedin-link"
                                className="group flex items-center gap-4 border border-white/10 bg-surface/70 p-5 transition-[border-color] duration-300 hover:border-neon/40"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-neon/30 bg-neon/5 text-neon">
                                    <Linkedin size={17} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-500">LinkedIn</span>
                                    <span className="break-all font-mono2 text-sm text-zinc-200 transition-colors duration-300 group-hover:text-neon">{CONTACT.linkedin}</span>
                                </span>
                            </a>
                            <a
                                href={CONTACT.githubHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-github-link"
                                className="group flex items-center gap-4 border border-white/10 bg-surface/70 p-5 transition-[border-color] duration-300 hover:border-neon/40"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-neon/30 bg-neon/5 text-neon">
                                    <Github size={17} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block font-mono2 text-[11px] uppercase tracking-[0.2em] text-zinc-500">GitHub</span>
                                    <span className="break-all font-mono2 text-sm text-zinc-200 transition-colors duration-300 group-hover:text-neon">{CONTACT.github}</span>
                                </span>
                            </a>
                            <a
                                href="/resume.pdf"
                                download
                                data-testid="contact-download-resume-btn"
                                className="flex items-center justify-center gap-2 border border-neon px-5 py-4 font-mono2 text-sm text-neon transition-colors duration-300 hover:bg-neon hover:text-black"
                            >
                                <Download size={15} strokeWidth={1.5} />
                                Download Resume
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.12} className="lg:col-span-7">
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            noValidate
                            className="border border-white/10 bg-surface/70 p-7 sm:p-9"
                        >
                            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="contact-name" className="mb-2 block font-mono2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                                        Name
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-name-input"
                                        type="text"
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your name"
                                        aria-invalid={!!errors.name}
                                        className={inputClass}
                                    />
                                    {errors.name && <p data-testid="contact-name-error" className="mt-2 font-mono2 text-xs text-red-400">{errors.name}</p>}
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-2 block font-mono2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                                        Email
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-email-input"
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@example.com"
                                        aria-invalid={!!errors.email}
                                        className={inputClass}
                                    />
                                    {errors.email && <p data-testid="contact-email-error" className="mt-2 font-mono2 text-xs text-red-400">{errors.email}</p>}
                                </div>
                            </div>
                            <div className="mt-5">
                                <label htmlFor="contact-subject" className="mb-2 block font-mono2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                                    Subject
                                </label>
                                <input
                                    id="contact-subject"
                                    data-testid="contact-subject-input"
                                    type="text"
                                    value={form.subject}
                                    onChange={set("subject")}
                                    placeholder="What's this about?"
                                    aria-invalid={!!errors.subject}
                                    className={inputClass}
                                />
                                {errors.subject && <p data-testid="contact-subject-error" className="mt-2 font-mono2 text-xs text-red-400">{errors.subject}</p>}
                            </div>
                            <div className="mt-5">
                                <label htmlFor="contact-message" className="mb-2 block font-mono2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                                    Message
                                </label>
                                <textarea
                                    id="contact-message"
                                    data-testid="contact-message-input"
                                    rows={6}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="Your message..."
                                    aria-invalid={!!errors.message}
                                    className={`${inputClass} resize-y`}
                                />
                                {errors.message && <p data-testid="contact-message-error" className="mt-2 font-mono2 text-xs text-red-400">{errors.message}</p>}
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-submit-button"
                                disabled={sending}
                                className="mt-7 flex w-full items-center justify-center gap-2 bg-neon px-6 py-4 font-mono2 text-sm font-semibold text-black transition-colors duration-300 hover:bg-cyber disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                            >
                                {sending ? (
                                    <>
                                        <Loader2 size={15} className="animate-spin" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <Send size={15} strokeWidth={1.5} />
                                        Send Message
                                    </>
                                )}
                            </button>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
